/**
 * 함우리몰 관리 화면 기능정의서.
 * 판매관리센터·관리자 셸 우측 상단 패널이 route name 기준으로 이 내용을 보여 준다.
 * target 값은 화면의 data-tour 속성과 연결되어 튜토리얼 하이라이트에 쓰인다.
 */

export interface FeatureSpecItem {
  name: string
  description: string
  /** data-tour 셀렉터 값. 화면의 해당 UI를 하이라이트한다 */
  target?: string
  /** 검색 보조어 (동의어·업무 용어) */
  keywords?: string[]
}

export interface FeatureSpec {
  title: string
  summary: string
  audience: 'seller' | 'admin'
  purpose: string
  features: FeatureSpecItem[]
  notes?: string[]
  /** 화면 단위 검색 보조어 */
  keywords?: string[]
}

export type FeatureSearchHitKind = 'page' | 'feature' | 'note'

export interface FeatureSearchHit {
  routeName: string
  audience: 'seller' | 'admin'
  pageTitle: string
  kind: FeatureSearchHitKind
  score: number
  title: string
  snippet: string
  matchedIn: string
  featureName?: string
  target?: string
}

export const MANAGE_FEATURE_SPECS: Record<string, FeatureSpec> = {
  /* ========================= 판매관리센터 ========================= */
  'seller-dashboard': {
    title: '판매 대시보드',
    summary: '입금 확인된 주문·재고·문의 등 오늘 처리할 일을 한 화면에서 본다.',
    audience: 'seller',
    purpose: '로그인 직후 우선 처리할 업무를 파악하고 관련 화면으로 이동한다.',
    keywords: ['홈', '오늘 할 일', '기능검색', '기능 정의'],
    features: [
      {
        name: '매출 추이',
        description: '기간별 매출·주문 건수와 일별 추이 차트를 확인한다.',
        target: 'seller-dash-stats',
      },
      {
        name: '바로 처리할 일',
        description:
          '신규·발송대기·배송중·클레임·재고·미답변을 누르면 해당 상태·조건이 적용된 화면으로 이동한다.',
        target: 'seller-dash-tasks',
      },
      {
        name: '주문 상태',
        description: '최근 90일 주문 상태 분포를 보고, 조각을 누르면 해당 상태 주문·클레임 화면으로 이동한다.',
        target: 'seller-dash-status',
      },
      {
        name: '잘 팔리는 상품',
        description: '최근 30일 매출 상위 상품을 보고 상품 수정 화면으로 이동한다.',
        target: 'seller-dash-top',
      },
      {
        name: '재고 확인',
        description:
          '재고가 적은 상품부터 보여 준다. 행을 누르면 상품관리에서 해당 상품 재고·이력 모달이 열린다(?stock=low&code=). 「전체 재고」는 저재고 필터만 연다.',
        target: 'seller-dash-stock',
        keywords: ['저재고', '품절', '최소재고', '재고이력'],
      },
      {
        name: '최근 주문',
        description: '입금 확인된 최근 주문을 보고 주문 관리로 이동한다.',
        target: 'seller-dash-orders',
      },
      {
        name: '정산 예정',
        description: '입금 확인된 주문 기준 예상 정산액을 본다.',
        target: 'seller-dash-payout',
      },
      {
        name: '최근 리뷰',
        description: '최근 후기와 평균 별점을 보고 해당 상품 후기 화면으로 이동한다.',
        target: 'seller-dash-reviews',
      },
      {
        name: '알림 벨',
        description:
          '상단 벨에서 처리할 일과 최근 알림을 본다. 설정에서 켠 유형(주문·클레임·문의 등)만 쌓인다.',
        target: 'seller-notify-panel',
        keywords: ['벨', '알림'],
      },
      {
        name: '기능 검색',
        description:
          '우측 상단 「도움말」에서 키워드로 전체 화면 기능을 찾고, 결과로 이동·하이라이트한다. Ctrl+K(Mac: ⌘K)로 바로 연다.',
        target: 'manage-feature-search',
        keywords: ['검색', '찾기', '튜토리얼', '도움말', '기능정의'],
      },
      {
        name: '최고관리자 보기',
        description:
          '최고관리자가 대신 보고 있으면 안내 막대가 뜨고, 관리자 화면으로 돌아갈 수 있다.',
        target: 'seller-viewing-as-admin',
      },
    ],
    notes: [
      '입금 전(PENDING) 주문은 판매업체에 노출되지 않는다.',
      '상단 알림 벨은 설정에서 켠 유형만 쌓인다.',
      '최고관리자가 설정·콘텐츠의 「판매업체 화면 보기」로 들어오면 상단에 안내 막대가 보인다.',
    ],
  },
  'seller-orders': {
    title: '주문 관리',
    summary: '입금 확인된 주문을 기간·상태로 찾고 발송·송장·재발송·피킹까지 처리한다.',
    audience: 'seller',
    purpose: '구매 기관에 상품을 제때 보내고 배송 상태를 갱신한다.',
    keywords: ['송장', '발송', '추가발송', '누락분 재발송', '누락', '피킹', '출고', 'CSV'],
    features: [
      {
        name: '상태 탭',
        description: '신규·발송준비·배송중·배송완료·취소로 주문을 나눈다. 구매 기관이 배송 전에 환불신청한 건은 「취소」에 바로 보인다.',
        target: 'seller-orders-tabs',
        keywords: ['취소', '환불신청', '상태'],
      },
      {
        name: '기간·검색',
        description:
          '1주일~전체 프리셋과 시작·종료일, 주문번호·기관·상품·수령인 검색으로 좁힌다. 알림에서 주문번호를 누르면 해당 주문이 바로 강조된다.',
        target: 'seller-orders-toolbar',
      },
      {
        name: 'CSV 내려받기',
        description: '현재 필터·기간에 맞는 주문 목록을 CSV로 내려받는다.',
        target: 'seller-orders-export',
        keywords: ['CSV', '엑셀', '다운로드'],
      },
      {
        name: '주문 목록',
        description:
          '구매 기관·배송지·수량·정산 예정액을 확인하고, 신규는 「주문 확인」을 먼저 누른다. 확인 뒤에 「발송 처리」가 나온다. 송장이 있으면 「수정」으로 고친다. 행을 선택하면 주문 확인·일괄 발송·피킹리스트 인쇄가 나타난다. 택배 발송 후 진행·도착은 송장 조회로 자동 반영된다.',
        target: 'seller-orders-table',
        keywords: ['일괄', '피킹', '송장 붙여넣기', '인쇄', '출고', '배송완료', '구매기관', '주문 확인'],
      },
      {
        name: '주문 확인',
        description:
          '신규 주문은 「주문 확인」을 크게 눌러 봤다는 표시를 남긴다. 확인 전에는 발송 처리가 나오지 않는다.',
        target: 'seller-orders-confirm',
        keywords: ['주문 확인', '신규', 'READY'],
      },
      {
        name: '배송 자동 반영',
        description:
          '주문 확인 뒤에 택배사·송장만 넣으면 진행은 자동 조회된다. 택배사는 점유·인지도 순으로 CJ대한통운·한진·롯데·우체국·로젠·경동·CU편의점·GS편의점·대신·일양·천일·직접배송이다. 도착하면 배송완료로 바뀐다. 구매확정되면 그달 정산에 잡힌다. 직접배송만 도착 후 「배송완료」를 누른다.',
        target: 'seller-orders-deliver',
        keywords: ['배송완료', '도착', '정산', '송장', '자동조회', '택배사', '경동', '대신', '일양', 'CU', 'GS', '천일'],
      },
      {
        name: '직접배송 안내',
        description:
          '목록 위에 직접배송은 도착 조회가 없어 「배송완료」를 직접 눌러야 정산에 잡힌다는 안내를 둔다.',
        target: 'seller-orders-direct',
        keywords: ['직접배송', '배송완료', '수동'],
      },
      {
        name: '재발송 등록',
        description:
          '오발송·누락으로 택배를 한 번 더 보냈을 때 「재발송 등록」으로 다른 송장을 붙인다. 주문번호는 그대로다. 카톤 단위는 주문 수량만 묶고 배송을 나누지 않는다. 송장이 하나면 송장번호만 보이고, 재발송이 있을 때만 본발송·재발송으로 나뉜다.',
        target: 'seller-orders-extra-ship',
        keywords: ['추가발송', '재발송', '누락', '오발송', '수량부족', '카톤', '송장'],
      },
      {
        name: '배송조회',
        description:
          '각 송장 옆에 「배송완료·배송중·조회 전」 배지가 붙어 본발송/재발송 각각의 현재 상태를 한 눈에 본다. 송장번호나 「배송조회」를 누르면 그 송장의 배송일지가 열리고, 조회 결과는 해당 송장에 저장되어 다음에도 유지된다. 자동 조회는 항목 대표 송장뿐 아니라 본발송·재발송 각각을 개별로 확인한다. 구매 기관·관리자와 같은 화면이다.',
        target: 'seller-orders-tracking',
        keywords: ['배송조회', '송장', '택배', '배송일지', '본발송', '재발송', '배송완료', '배송중'],
      },
      {
        name: '주문별 상품문의 칩',
        description:
          '주문번호 셀에 미답변 상품문의가 있으면 「문의 N」 칩이 뜬다. 칩을 누르면 「리뷰 · 문의」 화면에서 해당 주문번호로 필터되어 바로 답변할 수 있다. 문의는 판매업체 본인 상품 것만 셈에 넣는다.',
        target: 'seller-orders-table',
        keywords: ['문의', '상품문의', '주문 문의', 'QnA'],
      },
      {
        name: '기능 검색',
        description:
          '우측 상단 「도움말」에서 키워드로 기능을 찾고 위치를 표시한다. Ctrl+K(Mac: ⌘K).',
        target: 'manage-feature-search',
        keywords: ['검색', '찾기', '튜토리얼', '도움말', '기능정의'],
      },
    ],
    notes: [
      '관리자가 입금 확인한 뒤에만 목록에 나타난다. 그때 등록 이메일·휴대폰으로 신규 주문 안내가 간다. 결제확인이 취소되면 목록에서 사라지고 안내 메일·문자가 간다. 구매 기관이 배송 전 환불신청하거나 관리자가 주문을 취소하면 「취소」 탭에 남고 안내가 간다.',
      '구매 기관 환불신청: 입금 확인·발송준비(READY)까지 가능하고 배송중에는 불가하다. 신청 즉시 주문은 취소된다. 발송준비 중이면 판매업체에 취소 문자가 가고, 운영 휴대폰에도 환불신청 문자가 간다. 신규(미확인) 단계는 문자 없이 취소만 반영한다.',
      '배송이 시작된 뒤에는 배송완료 후 반품 요청만 가능하다.',
      '세금계산서 요청·발행은 구매 기관과 관리자만 본다. 판매업체 화면·알림·CSV에는 넣지 않는다.',
      '일괄 처리·피킹리스트는 주문을 하나 이상 선택했을 때만 보인다.',
      '발송 후에는 배송중이다. 택배 송장이 있으면 도착이 조회되는 즉시 배송완료로 바뀐다. 구매확정된 주문 건수가 그달 정산에 잡힌다. 신규(미확인)는 주문 확인 전에는 발송할 수 없다.',
      '발송 처리 시 구매 기관 연락처로 발송 안내 문자·메일이 간다. 문자에는 택배사·송장과 함우리몰 공개 배송조회(/mall/tracking) 링크가 포함된다.',
      '오발송·누락 재발송은 새 주문이 아니다. 같은 주문에 송장만 추가되고, 재발송이 있을 때만 본발송과 재발송이 나뉜다.',
      '사이드바 「주문 관리」 숫자 배지는 신규(입금확인)·발송준비 등 아직 처리하지 않은 건수다.',
    ],
  },
  'seller-claims': {
    title: '취소 · 반품',
    summary: '구매 기관의 취소·반품 요청을 기간·상태로 보고 승인하거나 반려한다.',
    audience: 'seller',
    purpose: '클레임을 신속히 처리해 정산·재고·고객 응대를 맞춘다.',
    keywords: ['클레임', '취소', '반품', 'CS', '반려'],
    features: [
      {
        name: '현황 요약',
        description: '처리 대기·취소·반품·완료 건수를 한눈에 본다.',
        target: 'seller-claims-kpis',
      },
      {
        name: '상태 탭',
        description: '처리 대기·취소 요청·반품 요청·승인 완료·반려·전체로 나눈다.',
        target: 'seller-claims-tabs',
      },
      {
        name: '기간·검색',
        description: '요청일 기간과 주문번호·상품·기관명으로 목록을 좁힌다.',
        target: 'seller-claims-toolbar',
      },
      {
        name: '요청 목록',
        description: '카드형 목록에서 사유를 확인하고 승인·반려한다. 반려 건은 「반려」 탭에서 다시 볼 수 있다.',
        target: 'seller-claims-list',
      },
    ],
    notes: [
      '취소·반품 요청이 들어오면 판매관리센터 알림과 함께, 「취소 · 반품 요청」을 켜 둔 업체의 이메일·휴대폰으로 안내한다.',
      '배송 전 구매자 환불신청은 요청과 동시에 취소 완료로 반영된다. 반품·기존 취소 요청만 승인·반려한다.',
      '응답이 늦거나 분쟁이 있으면 운영팀이 대신 처리할 수 있다.',
      '반려해도 이력이 남아 「반려」 탭에서 사유를 확인할 수 있다.',
      '대시보드에서 취소/반품 건수를 누르면 type 쿼리로 해당 탭이 열린다.',
      '반품(RETURN) 승인 시 정산만 차감되고 재고는 자동 복구되지 않는다. 실물 입고 후 상품관리에서 재고를 눌러 이력 모달로 추가한다. 취소 승인은 재고가 복구되며 이력에 「복구」로 남는다.',
    ],
  },
  'seller-products': {
    title: '상품 관리',
    summary: '상시몰·공동구매몰 상품을 한 목록에서 보고, 상품을 찍으면 작업판에서 조회·재고·평점·수정을 처리한다. 넓은 화면에서는 작업판이 오른쪽, 좁으면 목록 아래다.',
    audience: 'seller',
    purpose: '판매 가능한 상품을 유지하고 품절·숨김·등록·수정을 빠르게 한다.',
    features: [
      {
        name: '작업판',
        description:
          '줄을 누르면 작업판에 그 상품 하나만 열린다. 넓은 화면에서는 오른쪽, 좁으면 목록 아래다. 이때만 수정·몰 미리보기·복제·삭제와 그 상품의 검수 요청·철회·판매상태 변경을 한다. 상태 칸은 현재 상태(임시저장·반려·검수요청)를 보여 주고, 임시저장·반려일 때만 「검수 요청하기」로 검수를 맡긴다. 둘 이상을 체크하면 작업판은 선택 목록으로 바뀌고, 가능한 상태 변경만 보여 준다.',
        target: 'seller-products-bench',
        keywords: ['하위', '메뉴', '판매건수', '조회', '평점', '재고', '등록'],
      },
      {
        name: '판매 몰',
        description:
          '목록은 상시몰·공동구매몰을 함께 보는 전체 몰이 기본이다. 몰 컬럼에 구분이 보인다. 공동구매 상품은 특별할인에 묶이지 않고, 특별할인마다 그대로 보인다. 조건의 판매 몰로 한 몰만 좁힐 수 있다. 상품 등록은 상시몰로만 열린다. 공동구매몰은 잠시 새 상품을 올리지 못한다. 이미 공동구매인 상품은 그 구분이 유지된다.',
        target: 'seller-products-mall',
        keywords: ['상시몰', '공동구매몰', '판매 구분', '조건'],
      },
      {
        name: '조건',
        description:
          '상태·판매 몰·카테고리·재고는 각각 전체가 기본이고, 고른 조건은 함께 적용된다. 상태는 전체·검수요청·판매중·품절·반려·숨김·임시저장이다. 재고의 저재고는 상태와 따로 고른다. 상품명 검색과 정렬(최근 등록·판매량·재고·판매가)도 같은 조건창에 있다. 전체가 아닌 값이 있으면 「전체로」로 되돌린다. ?stock=low 로 들어오면 재고만 저재고다.',
        target: 'seller-products-toolbar',
        keywords: ['정렬', '판매가', '재고', '저재고', '검수', '칩'],
      },
      {
        name: 'CSV 내려받기',
        description: '현재 목록 조건의 상품을 CSV로 내려받는다.',
        target: 'seller-products-export',
        keywords: ['CSV', '엑셀', '다운로드'],
      },
      {
        name: '상품 등록 · 수정 모달',
        description:
          '목록을 유지한 채 모달에서 등록·수정한다. ?form=new 또는 ?form=상품코드로도 연다. 신규 등록은 바깥 클릭·Esc로 닫히지 않고 X·취소로만 닫는다. 수정 모달은 바깥 클릭·Esc로 닫히며, 미저장이면 확인한다.',
        target: 'seller-products-new',
        keywords: ['모달', '등록', '수정', '검수 요청'],
      },
      {
        name: '여러 상품 상태',
        description:
          '둘 이상을 체크하면 오른쪽이 선택 목록으로 바뀐다. 버튼 숫자는 실제로 그 상태로 바뀌는 상품만 센다. 임시저장·반려는 「검수 요청하기」, 검수요청은 철회, 이미 검수된 상품만 판매 재개·중지다. 이미 그 상태인 상품과 재고 없는 품절·숨김은 판매 재개에 넣지 않는다. 수정·미리보기·복제·삭제는 없다. 목록 조건이 바뀌면 화면에서 사라진 상품은 선택에서 빠진다.',
        target: 'seller-products-bulk',
        keywords: ['일괄', '검수요청', '선택', '체크'],
      },
      {
        name: '상품 목록',
        description:
          '상품명·코드·분류, 몰(상시몰·공동구매몰), 판매가, 재고, 상태를 본다. 목록이 좁으면 같은 항목을 칸 대신 한 줄 덩어리로 보여 준다. 줄을 누르면 체크가 풀리고 그 상품만 작업판에 열린다. 체크는 여러 상품의 상태를 한꺼번에 바꿀 때만 쓴다. 공동구매 상품의 몰 칸에는 공동구매몰만 보인다.',
        target: 'seller-products-table',
        keywords: ['재고', '입고', '판매이력', '조회수', '평점', '후기', '일괄', '판매 재개', '판매 중지', '판매가', '몰 노출', '검수요청'],
      },
      {
        name: '재고 · 판매 이력',
        description: '작업판의 재고를 누르면 현재 재고·누적 판매, 입금 확인된 판매 차감, 입고·취소 복구 이력을 보고 재고를 추가한다. 상품 등록 수량이 첫 입고다. URL ?code= 로도 연다. 노출 상품은 내용 수정 화면에서도 같은 원장으로만 재고를 바꾼다.',
        target: 'seller-products-stock',
        keywords: ['재고', '입고', '판매', '이력', '복구', '초기입고'],
      },
      {
        name: '조회 · 전환',
        description: '작업판의 조회 또는 판매를 누르면 조회·판매·전환율을 보고 몰 미리보기로 이어진다.',
        target: 'seller-products-views',
        keywords: ['조회수', '전환율', '트래픽'],
      },
      {
        name: '평점 · 후기',
        description:
          '작업판의 평점을 누르면 평점·후기 건수·별점 분포를 모달로 본다. 자세히 보기로 해당 상품의 리뷰·문의 화면으로 이동한다.',
        target: 'seller-products-rating',
        keywords: ['평점', '후기', '리뷰'],
      },
      {
        name: '몰 미리보기',
        description:
          '작업판의 몰 미리보기를 누르면 모달로 몰 상세·구매후기·상품문의·배송안내를 확인한다. 몰 탭을 열지 않아도 된다.',
        target: 'seller-products-preview',
        keywords: ['미리보기', '후기', '문의', '상세'],
      },
      {
        name: '상품 복제',
        description: '작업판에서 기존 상품을 복제해 새 등록 폼을 연다.',
        target: 'seller-products-clone',
        keywords: ['복제', '복사'],
      },
      {
        name: '화면 도움말',
        description:
          '우측 상단 「도움말」을 눌러 이 화면 안내를 보거나, 검색으로 다른 화면 기능을 찾아 위치를 표시한다.',
        target: 'manage-feature-search',
        keywords: ['검색', '찾기', '튜토리얼', '도움말', '기능정의'],
      },
    ],
    notes: [
      '신규 등록·판매가/공급가/마진 변경은 운영자 검수 후 몰에 노출된다. 그 외 수정(이름·이미지·옵션·주문 단위·배송 등)은 자동 승인되어 저장 즉시 반영된다.',
      '등록·수정 UI는 모달이며 /seller/products?form=… 로 연다.',
      '작업판의 몰 미리보기는 숨김·검수 대기 상품도 볼 수 있다.',
      '둘 이상 체크하면 수정·미리보기·복제·삭제는 나오지 않는다. 검수 요청하기·철회·판매 재개·중지는 그 선택에서 가능한 것만 건수와 함께 나온다.',
    ],
  },
  'seller-product-new': {
    title: '상품 등록',
    summary:
      '단계별 편집으로 판매 방식을 정한 뒤, 필요하면 전체 편집에서 상세·옵션을 보강하고 검수를 요청한다.',
    audience: 'seller',
    purpose: '몰에 올릴 판매글을 정확히 등록한다. 어떻게 팔지가 가격·수량·배송의 기준이다.',
    features: [
      {
        name: '단계별 편집',
        description:
          '대표 사진 등록 -> 기본 정보(상품명·분류·스펙표) -> 판매 방식(개당/박스·최소주문·카톤단위·옵션) -> 가격 및 재고 -> 상세설명(블록·태그) -> 배송 및 반품·메모를 순서대로 작성한다. 전체 편집의 모든 항목을 단계별로 한눈에 작성할 수 있다.',
        target: 'product-register-wizard',
        keywords: ['단계별 편집', '마법사', '질문', '단계', '개당', '박스', '최소주문', '카톤', '주문단위', '점검', '옵션', '스펙', '상세설명'],
      },
      {
        name: '단계별 편집 · 전체 편집',
        description: '상단에서 단계별 편집과 전체 편집을 오간다. 수정 때도 단계별 편집으로 묶음마다 다시 확인한다.',
        target: 'product-wizard-toggle',
        keywords: ['단계별 편집', '마법사', '전체편집', '전환'],
      },
      {
        name: '판매 방식',
        description:
          '판매 구분은 상시몰만 새로 고른다. 공동구매몰은 잠시 막아 새 상품을 올릴 수 없다. 이미 공동구매인 상품만 그 구분과 따뜻한 배경이 유지된다. 상시몰은 구매 수량에서 구성과 총액을 정하고, 그중 쇼핑몰 전면에 먼저 보여줄 구성을 고른다. 1개 구성은 없어도 된다. 목록과 상품 화면에는 고른 총액이 먼저 보이고, 개수·박스는 상품명에 적는다. 공동구매는 개·박스를 고르지 않고 1카톤에 들어 있는 개수만 적는다. 가격은 1개 가격이다. 목록에는 1개당 가격이, 상품 화면에는 카톤 가격과 묶음 개수가 보이며 최소 주문은 1카톤이다.',
        target: 'product-editor-sell',
        keywords: ['개당', '박스', '최소주문', '카톤', '주문단위', '10개', '사진', '판매단위'],
      },
      {
        name: '상품 편집기',
        description:
          '입력 순서는 상품 기본 정보(이미지·재고 포함), 상세 정보, 배송 정보, 가격 정보이다. 구매 수량 구성은 가격 정보 안에 있다. 상시몰은 개당 배송비 수량의 금액을 적은 뒤 구성 가격을 정한다. 구매 수량 구성은 빼도 되고, 각 구성의 「목록에 표시」로 하나를 고른다. 그 가격이 목록에 보이고, 상품 화면에서는 그 옵션이 미리 선택된다. 1개 구성은 없어도 된다. 마진 배분율은 입력칸과 그래프 없이 가격 카드 하단에 50%로 보인다. 개당 배송비 수량과 금액을 적기 전에는 공급가와 판매가격을 쓸 수 없다. 둘 다 적으면 그 개수의 공급가와 판매가격을 묻는다. 공급가와 판매가격 중 하나라도 없으면 구매 옵션을 쓸 수 없다. 그 금액은 배송비를 뺀 값이고, 가격 칸은 쉼표가 보이고, 고치는 동안에는 쉼표가 빠진다. 0원이면 입력칸 테두리가 빨갛다. 구성 가격에는 배송비를 넣는다. 1개 공급가·1개 판매가 칸은 없다. 자동옵션생성을 누르면 개당 배송비 수량까지의 구성이 추천 판매가격으로 들어간다. 자동계산은 이미 있는 옵션의 금액만 다시 넣는다. 개수는 입력해서 추가한다. 구성 아래에는 개당공급가, 배송비, 개당판매가, 개당마진이 보인다. 개당마진은 판매가에서 공급가와 배송비를 뺀 금액의 절반이다. 배송 카드 오른쪽 위에는 * 무료배송이 보인다. 출고 소요만 있고 비어 있다. 예시 문구를 고르거나 직접 적는다. 새 상품은 10개당 배송비 4,500원으로 시작하고 옵션은 비어 있다. 개당 배송비를 적으면 그 금액은 판매가격과 환불 배송비에 같이 쓴다. 원산지는 미입력이 기본이고, 위생·의료·생활용품은 비워도 된다. 식품·건강은 국내산·수입산·혼합·기타 중 하나를 골라야 한다. 상세 설명은 본문 이미지를 올리고, 필요하면 소제목·문단·안내를 더한다. 공동구매는 특별할인을 고르지 않고, 공동구매로 등록되면 특별할인마다 보인다. 오른쪽 미리보기는 목록 카드와 구매 화면 두 탭으로, 쇼핑몰에서 고객이 보는 카드와 같다. Ctrl+S 임시저장·이미지 Ctrl+V·브라우저 자동보관을 쓴다. 최대 주문 수량은 없고 재고가 주문 상한이며 몰에 재고가 노출된다.',
        target: 'seller-product-editor',
        keywords: ['Ctrl+S', '붙여넣기', '자동보관', '옵션', '추가금', '재고', '목록 카드', '원가', '판매가', '원산지', '국내산', '수입산'],
      },
      {
        name: '배송 · 교환 · 반품',
        description:
          '고객에게는 무료배송이다. 카드 오른쪽 위에 * 무료배송이 보인다. 출고 소요만 예시 문구로 고르거나 30자 안에서 직접 적는다. 개당 배송비는 가격 정보에 적는다. 당일 출고 문구는 관리자 화면 멘트에서 정하며 비우면 상품상세에 안 나온다.',
        target: 'product-editor-shipping',
        keywords: ['배송비', '반품', '교환', '출고', '박스당'],
      },
      {
        name: '몰 화면 선택',
        description: '상단에서 상품 · 배송·교환 단계를 고른다.',
        target: 'product-editor-anchor',
        keywords: ['단계', '바로가기', '목록 카드', '상세', '배송'],
      },
      {
        name: '몰 미리보기',
        description:
          '넓은 화면에서는 오른쪽 미리보기가 입력을 따라오고, 좁은 화면에서는 입력 아래에 이어서 보인다.  목록 카드 탭은 쇼핑몰 목록의 상품 카드와 같고, 구매 화면 탭은 상품을 눌렀을 때 나오는 구매 카드와 같다. 가격·옵션·수량을 바꾸면 그 화면이 같이 바뀐다. 구매·찜·장바구니 버튼은 주문으로 이어지지 않는다.',
        target: 'seller-product-preview-map',
        keywords: ['미리보기', '카드', '상세', '배송', '단계', '모달'],
      },
      {
        name: '판매 단위당 수익',
        description:
          '상품 단계에서 모달 하단(저장 버튼 위)에 1개 또는 1박스 기준 마진·수익을 한 줄로 고정한다. 입력하면서도 스크롤에 가리지 않고 확인할 수 있다.',
        target: 'seller-product-margin',
        keywords: ['마진', '수익', '원가', '판매가'],
      },
      {
        name: '검수 요청 · 저장',
        description:
          '임시 저장과 검수 요청을 구분한다. 반려된 상품은 「검수 재요청」으로 보낸다. Ctrl+S로 바로 임시 저장하고, 필수 미충족 시 해당 구간을 강조·포커스한다.',
        target: 'seller-product-save',
        keywords: ['마진', 'Ctrl+S', '임시저장', '포커스'],
      },
      {
        name: '화면 도움말',
        description:
          '우측 상단 「도움말」을 눌러 이 화면 안내를 보거나, 검색으로 다른 화면 기능을 찾아 위치를 표시한다.',
        target: 'manage-feature-search',
        keywords: ['검색', '찾기', '튜토리얼', '도움말', '기능정의'],
      },
    ],
    notes: [
      '실제 UI는 /seller/products?form=new 모달이다. 목록 화면에서 도움말을 열면 목록 기능이 먼저 보인다.',
      '상품코드(MP…)는 저장 시 시스템이 자동 발급한다.',
      '편집은 몰 노출 화면(목록 카드 · 상세 · 배송) 단위로 나눈다.',
      '최근 등록값 불러오기·내 상품 복사는 신규 등록 모달 상단에서 쓸 수 있다.',
    ],
  },
  'seller-product-edit': {
    title: '상품 수정',
    summary: '단계별 편집으로 기존 답을 묶음마다 확인하거나 전체 편집에서 고친다. 판매가·공급가·마진이 바뀌면 관리자 재승인이 필요하고, 그 외는 저장 즉시 몰에 반영된다.',
    audience: 'seller',
    purpose: '어떻게 파는지와 가격·재고·상세 변경을 반영한다. 마진 관련 값을 바꾸면 재검수 후 다시 올린다.',
    features: [
      {
        name: '단계별 편집',
        description:
          '이미 저장된 답을 왼쪽에서 한눈에 보고, 사진·기본정보·스펙·옵션·가격·상세설명·배송까지 모든 내용을 단계별로 점검하고 수정한다.',
        target: 'product-register-wizard',
        keywords: ['단계별 편집', '마법사', '점검', '질문', '단계', '옵션', '상세설명'],
      },
      {
        name: '단계별 편집 · 전체 편집',
        description: '상단에서 단계별 편집과 전체 편집을 오간다.',
        target: 'product-wizard-toggle',
        keywords: ['단계별 편집', '마법사', '전체편집'],
      },
      {
        name: '판매 방식',
        description:
          '판매 구분은 상시몰만 새로 고른다. 공동구매몰은 잠시 막아 새 상품을 올릴 수 없다. 이미 공동구매인 상품만 그 구분과 따뜻한 배경이 유지된다. 상시몰은 구매 수량에서 구성과 총액을 고치고, 그중 쇼핑몰 전면에 먼저 보여줄 구성을 고른다. 1개 구성은 없어도 된다. 목록과 상품 화면에는 고른 총액이 먼저 보이고, 개수·박스는 상품명에 적는다. 공동구매는 1카톤 개수만 고친다. 목록에는 1개당 가격이, 상품 화면에는 카톤 가격과 묶음 개수가 보이며 최소 주문은 1카톤이다.',
        target: 'product-editor-sell',
        keywords: ['개당', '박스', '최소주문', '카톤', '주문단위', '10개', '사진'],
      },
      {
        name: '상품 편집기',
        description:
          '입력 순서는 상품 기본 정보(이미지·재고 포함), 상세 정보, 배송 정보, 가격 정보이다. 구매 수량 구성은 가격 정보 안에서 고친다. 반려 사유가 있으면 상단에 안내된다. 상시몰은 개당 배송비 수량의 금액을 적은 뒤 구성 가격을 정한다. 구매 수량 구성은 빼도 되고, 각 구성의 「목록에 표시」로 하나를 고른다. 그 가격이 목록에 보이고, 상품 화면에서는 그 옵션이 미리 선택된다. 1개 구성은 없어도 된다. 마진 배분율은 입력칸과 그래프 없이 가격 카드 하단에 50%로 보인다. 개당 배송비 수량과 금액을 적기 전에는 공급가와 판매가격을 쓸 수 없다. 둘 다 적으면 그 개수의 공급가와 판매가격을 묻는다. 공급가와 판매가격 중 하나라도 없으면 구매 옵션을 쓸 수 없다. 그 금액은 배송비를 뺀 값이고, 가격 칸은 쉼표가 보이고, 고치는 동안에는 쉼표가 빠진다. 0원이면 입력칸 테두리가 빨갛다. 구성 가격에는 배송비를 넣는다. 1개 공급가·1개 판매가 칸은 없다. 자동옵션생성을 누르면 개당 배송비 수량까지의 구성이 추천 판매가격으로 들어간다. 자동계산은 이미 있는 옵션의 금액만 다시 넣는다. 개수는 입력해서 추가한다. 구성 아래에는 개당공급가, 배송비, 개당판매가, 개당마진이 보인다. 개당마진은 판매가에서 공급가와 배송비를 뺀 금액의 절반이다. 배송 카드 오른쪽 위에는 * 무료배송이 보인다. 출고 소요만 있다. 저장된 문구가 있으면 그대로 보이고, 없으면 비어 있다. 예시 문구를 고르거나 직접 고칠 수 있다. 가격 정보에서 「10개당 배송비 얼마」를 고치고, 그 금액은 판매가격과 환불 배송비에 같이 쓴다. 원산지는 미입력이 기본이고, 위생·의료·생활용품은 비워도 된다. 식품·건강은 국내산·수입산·혼합·기타 중 하나를 골라야 한다. 상세 설명은 본문 이미지를 올리고, 필요하면 소제목·문단·안내를 더한다. 공동구매는 특별할인을 고르지 않고, 공동구매로 등록되면 특별할인마다 보인다. 오른쪽 미리보기는 목록 카드와 구매 화면 두 탭으로, 쇼핑몰에서 고객이 보는 카드와 같다. 최대 주문은 없고 재고가 주문 상한·몰 노출 기준이다.',
        target: 'seller-product-editor',
      },
      {
        name: '배송 · 교환 · 반품',
        description:
          '고객에게는 무료배송이다. 카드 오른쪽 위에 * 무료배송이 보인다. 출고 소요만 예시 문구로 고르거나 30자 안에서 직접 고친다. 개당 배송비는 가격 정보에서 고친다. 당일 출고 문구는 관리자 화면 멘트에서 정하며 비우면 상품상세에 안 나온다.',
        target: 'product-editor-shipping',
        keywords: ['배송비', '반품', '교환', '출고', '박스당'],
      },
      {
        name: '몰 화면 선택',
        description: '상단에서 상품 · 배송·교환 단계를 고른다.',
        target: 'product-editor-anchor',
        keywords: ['단계', '바로가기', '목록 카드', '상세', '배송'],
      },
      {
        name: '몰 미리보기',
        description:
          '넓은 화면에서는 오른쪽 미리보기가 입력을 따라오고, 좁은 화면에서는 입력 아래에 이어서 보인다.  목록 카드 탭은 쇼핑몰 목록의 상품 카드와 같고, 구매 화면 탭은 상품을 눌렀을 때 나오는 구매 카드와 같다. 가격·옵션·수량을 바꾸면 그 화면이 같이 바뀐다. 구매·찜·장바구니 버튼은 주문으로 이어지지 않는다.',
        target: 'seller-product-preview-map',
        keywords: ['미리보기', '카드', '상세', '배송', '단계', '모달'],
      },
      {
        name: '판매 단위당 수익',
        description:
          '상품 단계에서 모달 하단(저장 버튼 위)에 1개 또는 1박스 기준 마진·수익을 한 줄로 고정한다.',
        target: 'seller-product-margin',
        keywords: ['마진', '수익', '원가', '판매가'],
      },
      {
        name: '저장',
        description:
          '판매중·품절·숨김 상품 수정 화면 하단에 「저장」이 있다. 판매가·공급가·마진 배분율이 바뀌면 재검수(REQUESTED)로 보내며 승인 전까지 몰에서 내려간다. 그 외 수정(이름·설명·이미지·옵션·주문 단위·배송 등)은 자동 승인되어 저장 즉시 몰에 반영되고 판매 상태(판매중·품절·숨김)는 그대로 유지된다. 재고는 내용 저장으로 바꾸지 않고 「재고·이력에서 추가」로만 올린다(재검수 없음). 초안·반려 건은 임시 저장이 가능하다.',
        target: 'seller-product-save',
        keywords: ['재검수', '임시저장', '자동 승인', '마진', '판매가', '원가', '몰 비노출', '저장'],
      },
      {
        name: '화면 도움말',
        description:
          '우측 상단 「도움말」을 눌러 이 화면 안내를 보거나, 검색으로 다른 화면 기능을 찾아 위치를 표시한다.',
        target: 'manage-feature-search',
        keywords: ['검색', '찾기', '튜토리얼', '도움말', '기능정의'],
      },
    ],
    notes: [
      '실제 UI는 /seller/products?form=상품코드 모달이다. 목록 화면에서 도움말을 열면 목록 기능이 먼저 보인다.',
      '미저장 변경 후 모달을 닫으면 확인한다.',
      '승인된 상품(판매중·품절·숨김)은 모달에서 판매 상태를 바로 바꿀 수 있다. 검수 전·반려에는 없다.',
      '노출 상품 재고는 「재고·이력에서 추가」만 쓴다. 품절에서 재고를 채우면 재입고 알림을 신청한 구매 기관에 알림이 간다.',
      '상품 등록 때 넣은 수량이 첫 입고로 이력에 남는다. 예전 상품은 이력 조회 시 등록 시점·추정 수량으로 첫 입고가 복원된다.',
    ],
  },
  'seller-settlements': {
    title: '정산 관리',
    summary: '구매확정 건과 반품으로 남은 금액을 골라 묶음으로 정산 요청하고, 요청별 엑셀을 받습니다.',
    audience: 'seller',
    purpose: '아직 받을 금액과 주문·상품별 수수료·지급 여부를 한눈에 본다.',
    keywords: ['입금', '미정산', '수수료', '판매대금', '정산금', '계좌', '건별', '배송비'],
    features: [
      {
        name: '기간 합계표',
        description:
          '선택한 구매확정 연월의 입금 대기·정산 완료·합계를 표로 본다. 연월은 표 제목과 각 행에 같이 적힌다. 계좌가 없으면 설정으로 등록한다.',
        target: 'seller-settle-summary',
        keywords: ['받을 금액', '계좌', '등록', '연월', '정산완료'],
      },
      {
        name: '정산 흐름',
        description: '구매확정 건과 반품으로 남은 금액을 고르면 하나의 정산 요청이 되고, 이름은 요청 시각과 건수로 붙습니다. 엑셀로 그 묶음을 받습니다.',
        target: 'seller-settle-howto',
      },
      {
        name: '상태·기간 필터',
        description: '전체·정산 완료·미정산과 구매확정 연월·주문번호·상품으로 좁힌다. 기본은 전체.',
        target: 'seller-settle-filters',
        keywords: ['필터', '검색'],
      },
      {
        name: '검색·새로고침',
        description: '기간·키워드로 다시 조회한다.',
        target: 'seller-settle-toolbar',
      },
      {
        name: '주문건별 정산 목록',
        description:
          '주문번호·상품·판매대금·함우리 수수료·판매업체 마진·배송비·지급액·주문/발송/완료일·정산 여부를 본다.',
        target: 'seller-settle-list',
        keywords: ['지급액', '마진', '배송비'],
      },
    ],
    notes: [
      '구매확정된 건과, 배송 후 반품으로 남은 변심 반품비·출고 배송비를 정산 요청할 수 있습니다. 배송완료만 된 건은 구매확정 전에 없습니다.',
      '요청 뒤에 반품으로 금액이 바뀌면 입금 완료가 되지 않습니다. 그 요청을 취소하고 다시 신청합니다.',
      '건을 선택해 하나의 정산 요청으로 묶습니다. 요청 이름은 몇 년 몇 월 몇 일 몇 시 몇 분 몇 건에 대한 정산요청입니다.',
      '요청한 묶음은 엑셀로 받을 수 있습니다. 입금 전에는 요청을 취소할 수 있습니다.',
      '지급액 = 판매대금 − 함우리 수수료 + 고객이 낸 배송비. 배송비는 주문당 해당 업체에 한 번만 붙는다.',
    ],
  },
  'seller-reviews': {
    title: '리뷰 · 문의',
    summary: '상품문의·후기·1:1 상담을 한 화면의 CS 워크벤치에서 기간·키워드로 찾고 응대한다.',
    audience: 'seller',
    purpose: '구매 기관의 신뢰와 전환을 높인다.',
    features: [
      {
        name: '요약 KPI',
        description: '평균 별점·미답변 문의·답변 대기 후기를 보고 미처리 건으로 바로 이동한다.',
        target: 'seller-reviews-kpis',
      },
      {
        name: '기간·검색',
        description:
          '1주일~전체 프리셋과 시작·종료일, 상품명·내용·주문번호·기관명 검색으로 좁힌다. 상품관리·대시보드·주문화면에서 넘어오면 해당 상품·주문으로 자동 필터되고 기간이 「전체」로 확장된다.',
        target: 'seller-reviews-toolbar',
      },
      {
        name: '답변 템플릿',
        description: '자주 쓰는 문의·후기 답변 문구를 관리하고 답변란에 바로 넣는다.',
        target: 'seller-reviews-templates',
        keywords: ['템플릿', '자주쓰는답변'],
      },
      {
        name: '문의 · 후기 워크벤치',
        description:
          '왼쪽 문의 레인에는 판매업체가 직접 답하는 「상품문의」와 관리자가 응대하는 「1:1 상담」(관련 주문이 판매업체 본인 것일 때만)이 함께 뜬다. 소스는 초록(상품문의)·주황(1:1 상담) 칩으로 구분된다. 기본적으로 1:1 상담은 조회 전용이지만, 관리자가 답변 권한을 위임한 상담에는 「답변 위임」 파란 배지가 붙고 판매업체가 직접 답변 폼으로 처리할 수 있다. 이미 관리자가 답한(답변완료) 상담이어도 위임되어 있으면 추가 답변을 남길 수 있고, 판매업체가 남긴 답변은 상담 스레드에 「판매자」로 기록된다. 오른쪽 후기 레인은 별점·본문·주문번호까지 확인하고 바로 답변한다. 주문번호 칩을 누르면 판매 주문 화면에서 그 주문이 검색된다. 좁은 화면에서는 문의 아래에 후기가 이어져 둘 다 보인다.',
        target: 'seller-reviews-lanes',
        keywords: ['상품문의', '1:1 상담', '문의 공유', '관리자 응대', '답변 위임', '답변권한'],
      },
    ],
    notes: [
      '구매자 마이페이지는 「문의 내역」 하나에서 상품 문의와 주문 문의를 함께 남긴다. 이 화면은 답변 주체가 달라 상품문의와 몰 상담을 구분해 처리한다. 둘 다 주문번호 첨부가 필수다. 주문번호가 있는 상담은 해당 주문의 판매업체에게도 참고용으로 공유된다(기본 조회 전용, 관리자가 위임한 건은 판매업체가 직접 답변). 이미 답변완료인 상담도 위임되면 추가 답변을 남길 수 있다.',
      '「미답변 문의」 KPI 는 판매업체가 답할 몫만 센다 — 상품문의 미답변과, 관리자가 위임한 1:1 상담 미답변 합계.',
      '기존에 주문 없이 남은 상품문의는 「일반 문의」로 보인다. 관리자가 먼저 답한 문의는 이미 완료로 보인다.',
      '판매 주문 화면에서 「문의 N」 칩을 누르면 같은 주문번호로 이 화면이 열려 바로 확인·답변할 수 있다.',
      'URL 쿼리: tab=review|inquiry, filter=open|done|all, productCode, keyword',
    ],
  },
  'seller-settings': {
    title: '판매업체 정보',
    summary: '업체 정보·정산 계좌·배송·알림·체결 계약·약관을 관리·확인한다.',
    audience: 'seller',
    purpose: '주문·정산·배송에 쓰이는 기본 정보를 최신으로 유지하고, 동의한 계약서를 다시 본다.',
    features: [
      {
        name: '업체 정보',
        description: '상호·대표자·연락처·주소를 수정한다.',
        target: 'seller-settings-profile',
      },
      {
        name: '정산 계좌',
        description: '정산금 입금 계좌를 등록한다.',
        target: 'seller-settings-bank',
      },
      {
        name: '배송 · 반품 정책',
        description:
          '신규 상품 기본 배송비·반품비·출고 마감·교환/반품 안내를 설정한다. 당일 출고 문구는 관리자 화면 멘트에서 정하며, 비우면 상품상세에 안 나온다. 고객 결제 배송비는 상품의 배송 부과(주문당은 최댓값, 개·박스마다는 수량 계산)를 따른다.',
        target: 'seller-settings-shipping',
        keywords: ['배송비', '반품', '출고마감', '교환', '기본배송비'],
      },
      {
        name: '알림 받기',
        description:
          '신규 주문·취소/반품을 켜 두면 등록 이메일·휴대폰으로 안내한다. 가입 승인·반려·정지와 주문 취소는 스위치와 관계없이 메일로 간다. 재고·문의·정산은 메일을 보내지 않는다.',
        target: 'seller-settings-notify',
        keywords: ['알림', '벨', '주문 알림', '메일'],
      },
      {
        name: '계약 · 약관',
        description:
          '체결 당시 저장된 계약 본문·서명·PDF를 확인한다. CMS에서 계약이 바뀌어도 이미 체결한 내용은 그대로다. 서명 전이면 활성 계약서 본문만 열람한다.',
        target: 'seller-settings-contract',
        keywords: ['계약서', '약관', '서명', '공급계약', 'PDF', '스냅샷'],
      },
      {
        name: '저장',
        description: '변경한 업체 정보를 저장한다. 미저장 상태로 다른 메뉴로 이동하면 확인한다.',
        target: 'seller-settings-save',
      },
    ],
  },

  /* ========================= 관리자 ========================= */
  'admin-dashboard': {
    title: '관리자 대시보드',
    summary: '입금·검수·세금계산서·클레임·미출고 등 운영 현황과 처리 대기 큐를 한눈에 본다.',
    audience: 'admin',
    purpose: '운영자가 우선 처리할 업무를 파악하고 바로 이동한다.',
    keywords: ['홈', '운영', '기능검색', '기능 정의', '입금알림', '입금 미진행', '세금계산서'],
    features: [
      {
        name: '핵심 KPI',
        description:
          '입금 미진행·입금 알림·클레임·미출고·세금계산서·검수요청을 요약하고, 클릭 시 해당 목록으로 이동한다. 클레임은 주문 화면의 「처리할 일 · 클레임」으로 연다. 임시저장 상품은 포함하지 않는다.',
        target: 'admin-dash-kpi',
      },
      {
        name: '세금계산서 발행 대기',
        description:
          '발행 요청된 주문을 기관·상호·수신 이메일·금액과 함께 보고, 주문·입금 화면으로 바로 이동한다.',
        target: 'admin-dash-tax',
        keywords: ['세금계산서', '발행', '이메일'],
      },
      {
        name: '검수요청 큐',
        description: '판매업체가 검수 요청한 상품만 바로 열어 처리한다. 임시저장은 여기 나오지 않는다.',
        target: 'admin-dash-queue',
      },
      {
        name: '바로 처리할 일',
        description:
          '입금 미진행·입금 알림·상품 검수·클레임·미출고·세금계산서 화면으로 바로 이동한다. 클레임은 주문 화면 처리할 일로 연다.',
        target: 'admin-dash-links',
      },
      {
        name: '기능 검색',
        description:
          '우측 상단 「도움말」에서 키워드로 전체 화면 기능을 찾고, 결과로 이동·하이라이트한다. Ctrl+K(Mac: ⌘K)로 바로 연다.',
        target: 'manage-feature-search',
        keywords: ['검색', '찾기', '튜토리얼', '도움말', '기능정의'],
      },
    ],
  },
  'admin-products': {
    title: '상품 관리',
    summary: '상태·몰 노출·저재고·기간으로 상품을 찾고 검수·재고·복제·판매 상태를 운영한다.',
    audience: 'admin',
    purpose: '품질·가격·정보가 맞는 상품만 판매하고, 재고 원장을 판매업체와 같은 흐름으로 맞춘다.',
    keywords: ['검수', '재고', '복제', '특가', '배지', '저재고', '테스트상품'],
    notes: [
      '테스트상품은 최고관리자 admin 계정에서만 목록·상세에 보인다. 일반 관리자·일반 몰 방문자에게는 숨긴다.',
      '판매업체 화면의 등록 상품 수에서도 업체 검색어로 이 화면으로 들어올 수 있다.',
      '상품명 클릭 미리보기는 숨김·검수요청 상품도 볼 수 있다. 임시저장은 판매자가 검수 요청하기 전에는 목록에 없다.',
    ],
    features: [
      {
        name: '판매 상태 필터',
        description: '전체·판매중·검수요청·품절·숨김·반려 건수로 목록을 바로 좁힌다. 임시저장은 판매자가 검수 요청하기 전에는 나오지 않는다.',
        target: 'admin-products-status',
        keywords: ['검수요청', '검수 대기', '품절', '숨김'],
      },
      {
        name: '기간·검색·정렬',
        description:
          '요청일 기간 프리셋과 상품명·코드·업체로 목록을 좁힌다. 관리자 마진·판매가·판매량 순으로 정렬할 수 있다. URL keyword로도 진입한다.',
        target: 'admin-products-toolbar',
        keywords: ['마진', '정렬'],
      },
      {
        name: '몰 노출 필터',
        description: '이달의 특가·배지·일반 노출로 목록을 한 번 더 좁힌다. 「저재고」칩과 ?stock=low 로 재고가 적은 상품을 모은다.',
        target: 'admin-products-exposure',
        keywords: ['특가', '배지', '저재고'],
      },
      {
        name: '상품 목록',
        description:
          '관리자(함우리) 마진·판매상태·몰 노출중/비노출·특가·배지를 보고 검수·편집 화면으로 들어간다. 검수요청 횟수가 보이며, 재고·조회·리뷰 숫자를 누르면 각각 이력·전환·후기 미리보기가 열린다. 복제(임시저장), 내리기·재판매·재검수를 한다. 판매업체명을 누르면 그 업체로 검색된다. 상품명으로 몰 미리보기도 연다.',
        target: 'admin-products-table',
        keywords: ['재고', '복제', '입고', '판매이력', '조회수', '평점', '후기', '관리자 마진', '몰 노출', '검수요청'],
      },
      {
        name: '재고 · 판매 이력',
        description:
          '재고 숫자를 누르면 판매·입고·복구 이력을 보고 재고를 추가한다. 저재고 칩·?stock=low&code= 로도 연다. 판매업체와 같은 모달·원장이다.',
        target: 'admin-products-stock',
        keywords: ['재고', '입고', '판매', '이력', '저재고'],
      },
      {
        name: '조회 · 전환',
        description: '조회수를 누르면 조회·판매·전환율을 보고 몰 미리보기로 이어진다.',
        target: 'admin-products-views',
        keywords: ['조회수', '전환율'],
      },
      {
        name: '평점 · 후기',
        description: '리뷰·평점을 누르면 몰 미리보기의 구매후기 탭을 연다.',
        target: 'admin-products-rating',
        keywords: ['평점', '후기', '리뷰'],
      },
      {
        name: '몰 미리보기',
        description:
          '상품명을 누르면 모달로 몰 상세·구매후기·상품문의·배송안내를 확인한다. 몰에 들어가지 않아도 된다.',
        target: 'admin-products-preview',
        keywords: ['미리보기', '후기', '문의', '상세'],
      },
    ],
  },
  'admin-product-edit': {
    title: '상품 검수 · 편집',
    summary:
      '판매업체와 같은 수정 모달에서 판매 방식·상품·배송을 검수하고 승인·반려한다.',
    audience: 'admin',
    purpose: '구매자에게 노출될 최종 상품 정보를 완성한다.',
    features: [
      {
        name: '검수 모달 헤더',
        description:
          '상품 코드·상태·업체를 보고, 상품 · 배송·교환 단계 탭으로 전환한다.',
        target: 'admin-product-summary',
        keywords: ['검수', '상태', '단계'],
      },
      {
        name: '몰 화면 선택',
        description: '상품 · 배송·교환 단계를 고르고 완료 여부를 본다.',
        target: 'product-editor-anchor',
        keywords: ['앵커', '목록 카드', '상세', '배송'],
      },
      {
        name: '검수 내역',
        description:
          '헤더의 이력을 누르면 검수 이력이 따로 열린다. 요청·승인·반려 시각과 요청 메모를 타임라인으로 보고, 여러 번 요청한 건은 N차 검수 요청으로 구분한다.',
        target: 'admin-product-history',
        keywords: ['검수 이력', '재검수', '요청 메모', '타임라인'],
      },
      {
        name: '단계별 편집',
        description:
          '판매업체가 등록한 사진·기본정보·스펙·옵션·가격·상세설명·배송까지 모든 내용을 단계별로 검수하고 바로 수정할 수 있다.',
        target: 'product-register-wizard',
        keywords: ['단계별 편집', '마법사', '점검', '질문', '검수'],
      },
      {
        name: '단계별 편집 · 전체 편집',
        description: '상단에서 단계별 편집과 전체 편집을 오간다.',
        target: 'product-wizard-toggle',
        keywords: ['단계별 편집', '마법사', '전체편집'],
      },
      {
        name: '판매 방식',
        description:
          '판매 구분은 상시몰만 새로 고른다. 공동구매몰은 잠시 막아 새 상품을 올릴 수 없다. 이미 공동구매인 상품만 그 구분과 따뜻한 배경이 유지된다. 상시몰은 구매 수량과 전면으로 고른 구성 가격을 검수한다. 목록에는 그 총액이 먼저 보인다. 공동구매는 1카톤 개수와 1개 가격을 검수하고, 목록에는 1개당 가격이, 상품 화면에는 카톤 가격과 묶음 개수가 보인다.',
        target: 'product-editor-sell',
        keywords: ['개당', '박스', '최소주문', '카톤', '주문단위', '사진', '배송비'],
      },
      {
        name: '상품 편집',
        description:
          '상품 기본 정보, 상세 정보, 배송 정보, 가격 정보 순으로 수정한다. 배송 카드 오른쪽 위에는 * 무료배송이 보인다. 개당 배송비는 가격 정보에서 「10개당 배송비 얼마」처럼 고친다. 수량과 금액을 적기 전에는 공급가와 판매가격을 쓸 수 없고, 그 둘 중 하나라도 없으면 구매 옵션을 쓸 수 없다. 적은 배송비는 판매가격과 환불 배송비에 같이 쓴다. 가격은 판매 단위(1개/1박스) 기준이다. 오른쪽 미리보기는 목록 카드와 구매 화면 두 탭으로, 쇼핑몰에서 고객이 보는 카드와 같다. 표시 가격은 등록 판매가와 같다. 원산지는 미입력이 기본이고, 식품·건강만 검수 승인 전에 골라야 한다. 상세 설명은 본문 이미지를 올리고, 필요하면 소제목·문단·안내를 더한다. 공동구매는 특별할인을 고르지 않고, 공동구매로 등록되면 특별할인마다 보인다.',
        target: 'admin-product-editor',
        keywords: ['분류', '요약정보', '옵션', '추가금', '상세설명', '재고', '가격', '판매방식', '원산지', '국내산', '수입산'],
      },
      {
        name: '몰 미리보기',
        description:
          '넓은 화면에서는 오른쪽 미리보기가 입력을 따라오고, 좁은 화면에서는 입력 아래에 이어서 보인다.  목록 카드 탭은 쇼핑몰 목록의 상품 카드와 같고, 구매 화면 탭은 상품을 눌렀을 때 나오는 구매 카드와 같다. 가격·옵션·수량을 바꾸면 그 화면이 같이 바뀐다. 구매·찜·장바구니 버튼은 주문으로 이어지지 않는다.',
        target: 'seller-product-preview-map',
        keywords: ['미리보기', '목록 카드', '구매 화면', '상세'],
      },
      {
        name: '수익 바',
        description: '하단에서 1개당 함우리·판매업체 수익을 본다.',
        target: 'admin-product-margin',
        keywords: ['마진', '수익'],
      },
      {
        name: '내부 메모 · 관련상품',
        description:
          '배송 단계에서 관리자만 보는 내부 메모와 관련·대체 상품을 지정한다. 비우면 같은 분류 자동 추천이다.',
        target: 'admin-product-ops',
        keywords: ['내부메모', '관련상품', '대체상품'],
      },
      {
        name: '검수 확정',
        description:
          '취소는 왼쪽, 저장은 오른쪽이다. 반려와 판매 승인은 검수요청 상태에서만 오른쪽에 나온다. 저장하고 판매 승인 중에는 화면 전체가 로딩되고 닫을 수 없다. 필수 미충족 문구를 누르면 해당 입력으로 이동한다.',
        target: 'admin-product-actions',
        keywords: ['승인', '반려', '저장'],
      },
    ],
    notes: [
      '승인된 상품은 판매중·품절·숨김을 바로 바꿀 수 있다. 검수요청·반려에는 없다.',
      '검수요청 목록으로 들어왔을 때만 이전·다음 상품 이동이 보인다. 임시저장은 검수 화면을 열 수 없다.',
      '몰·판매업체·관리자 미리보기의 등록 판매가는 같다. 구매 화면도 그 판매가로 결제한다.',
    ],
  },
  'admin-deals': {
    title: '이달의 특가',
    summary: '몰 홈 히어로·특가 레일에 올릴 상품을 지정한다.',
    audience: 'admin',
    purpose: '운영자가 설정한 특가 상품만 홈 기획 영역에 노출한다.',
    features: [
      {
        name: '노출 목록',
        description: '순서·기간을 정하고 저장한다. 첫 번째가 히어로다. 행을 끌어다 놓아 순서를 바꿀 수 있다. 목록이 비면 판매많은순으로 홈이 채워진다.',
        target: 'admin-deals-list',
        keywords: ['드래그', '정렬', '순서'],
      },
      {
        name: '상품 추가',
        description: '판매중 상품을 검색해 특가 목록에 넣는다.',
        target: 'admin-deals-picker',
      },
    ],
    notes: ['특가 여부·기간은 이 화면에서만 설정한다.'],
  },
  'admin-sales': {
    title: '공동구매 특별할인',
    summary: '공동구매몰을 여는 특별할인 이름과 기간을 관리한다.',
    audience: 'admin',
    purpose: '설명절·추석명절·어린이날처럼 이벤트 특별할인의 이름과 시작·종료일을 정한다.',
    features: [
      {
        name: '특별할인 목록',
        description: '이름, 시작·종료일, 오늘 기준 예정·진행·종료를 본다. 이미 처리된 과거 주문은 공동구매이며 특별할인 이름은 비어 있다.',
        target: 'admin-sales-list',
        keywords: ['공동구매', '특별할인', '추석', '설명절', '어린이날', '기간'],
      },
      {
        name: '특별할인 등록 · 수정',
        description:
          '이름을 자유롭게 적고 시작일·종료일을 저장한다. 이름은 명절이거나 날짜 문구여도 된다. 상품은 특별할인에 묶이지 않으므로 특별할인을 지워도 공동구매 상품은 남는다.',
        target: 'admin-sales-new',
        keywords: ['등록', '기간', '이름', '삭제'],
      },
    ],
    notes: [
      '공동구매 상품은 등록·수정 화면에서 특별할인을 고르지 않는다. 공동구매로 등록되면 특별할인마다 그 상품이 보인다.',
      '새 주문은 주문 당시에 진행 중인 특별할인 이름과 코드를 고정한다.',
    ],
  },
  'admin-orders': {
    title: '주문 · 입금 확인',
    summary: '주문 상태와 처리할 일을 나눠 좁히고, 카드에서 주문을 펼쳐 판매업체·처리현황·배송을 한 화면에서 처리한다.',
    audience: 'admin',
    purpose: '입금·주문·세금계산서·배송·내부 메모를 한 자리에서 운영한다.',
    keywords: ['입금확인', '결제확인 취소', '처리현황', '세금계산서', '메모', 'CSV', '입금알림', '입금 미진행', '입금했어요', '배송', '판매업체', '펼치기', '환불', '수동 환불', '환불 완료', '추가발송', '재발송', '누락', '취소', '반품', '승인', '반려', '클레임', '문의', '처리할 일', '주문 상태', '조정', '전화 환불'],
    features: [
      {
        name: '주문 상태 필터',
        description:
          '전체·입금 알림·입금 미진행·배송 진행·완료·취소로 주문을 나눈다. 모든 주문은 이 다섯 상태 중 하나에만 들어간다. 「입금했어요」를 누른 건 입금 알림, 아직 안 누른 건 입금 미진행이다. 업체 미확인(결제확인만 된 주문)은 배송 진행에 포함한다.',
        target: 'admin-orders-status',
        keywords: ['입금 알림', '입금 미진행', '입금했어요', '배송 진행', '완료', '취소', '상태', 'pill', '필터'],
      },
      {
        name: '처리할 일 필터',
        description:
          '환불 대기·클레임·문의를 주문 상태와 다른 줄에 둔다. 누르면 송금·승인·미답변 문의처럼 아직 손이 가는 주문만 모은다. 같은 주문은 배송 진행에도, 환불 대기에도, 클레임·문의에도 각각 나올 수 있다.',
        target: 'admin-orders-work',
        keywords: ['환불 대기', '클레임', '문의', '세금계산서', '처리할 일'],
      },
      {
        name: '세금계산서 필터',
        description:
          '발행 요청(미완료) 주문만 모아 본다. pill을 누르면 세금계산서 표로 전환되고 모달에서 발행 완료·반려(문자 안내)를 처리한다. 「주문 펼치기」는 목록 카드를 펼친다.',
        target: 'admin-orders-tax',
        keywords: ['세금계산서', '발행', '반려', '문자', '발행완료'],
      },
      {
        name: '입금 계좌 바로가기',
        description: '툴바 오른쪽 컴팩트한 계좌 배지를 눌러 설정 화면으로 이동해 통장을 수정한다.',
        target: 'admin-orders-account',
        keywords: ['계좌', '설정'],
      },
      {
        name: '기간·검색',
        description:
          '기간 프리셋·날짜·주문번호·입금자·기관명으로 찾고 CSV로 내려받는다. 첫 진입은 최근 1개월로 좁혀 로딩 부담을 줄이고, 더 넓게 보려면 프리셋을 눌러 확장한다. 문의·CS·정산 등에서 「관련 주문」 링크로 넘어오면 이미 페이지가 열려 있어도 자동으로 「전체」 기간으로 다시 넓혀 검색된 주문이 기간 필터에 걸려 사라지지 않게 한다. 검색해도 결과가 없으면 「해당 주문번호를 찾지 못했습니다」 안내와 「검색 지우고 전체 보기」 버튼이 뜬다. 유일한 주문이 걸리면 자동으로 상세를 펼치고 그 카드로 부드럽게 스크롤한다.',
        target: 'admin-orders-toolbar',
      },
      {
        name: '주문 섹션 카드 목록',
        description:
          '입금 알림 → 입금 미진행 → 배송 진행 → 완료 → 취소 순서로 표시한다. 결제확인이 끝난 주문은 발송·배송 여부와 관계없이 「배송 진행」으로 모이고, 배송완료는 「완료」, 주문 취소는 「취소」에 둔다. 환불 대기·완료 칩은 주문 취소 왼쪽에 둔다. 입금 전에 취소되어 환불이 없으면 「입금 미진행」을 같은 자리에 붙인다. 카드 요약 어디를 눌러도 상세가 펼쳐진다.',
        target: 'admin-orders-table',
        keywords: ['입금 알림', '입금 미진행', '배송', '환불', '클레임', '완료', '취소', '카드', '펼치기', '상세', '입금확인', '세금계산서', '반려', '주문취소'],
      },
      {
        name: '카드 인라인 상세',
        description:
          '카드 요약을 누르면 아래로 상세가 펼쳐진다. 헤더에 이미 있는 상태 칩은 반복하지 않고, 환불 대기/완료 건수와 취소 사유만 한 줄로 덧붙인다. 이어서 주문 회원·발주번호·배송지·요청사항·주문시각·결제수단·입금자·결제확인을 촘촘한 표로 보여 준다. 세금계산서를 요청한 주문만 발행 정보를 펼치고, 미답변 문의가 있으면 질문만 붙인다. 판매업체별 상품표·결제 합계·내부 메모까지 한 자리에서 처리한다. 다른 화면에서 주문번호로 들어오거나 세금계산서 모달에서 「주문 펼치기」를 눌러도 같은 펼침 상세로 간다.',
        target: 'admin-orders-detail',
        keywords: ['상세', '펼치기', '판매업체', '메모', '함우리 마진', '사업자등록증', '세금계산서', '환불', '취소 사유', '발주번호'],
      },
      {
        name: '처리현황 스텝바',
        description:
          '카드 요약 하단에 4단계(입금대기·업체확인·발송·배송완료) 스텝을 두어 지금 어디에 있는지 즉시 본다. 배송이 끝났으면 배송완료로만 표시한다.',
        target: 'admin-orders-progress',
        keywords: ['처리현황', '스텝바', '발송', '송장', '업체 확인', '배송'],
      },
      {
        name: '배송조회',
        description:
          '각 송장 옆에 「배송완료·배송중·조회 전」 배지가 붙어 본발송/재발송의 현 상태가 카드만 봐도 분명하다. 상세의 송장번호나 「배송조회」를 누르면 그 송장의 배송일지가 열리고, 조회 결과는 해당 송장 컬럼에 저장되어 다음 열람에도 유지된다. 판매업체·구매 기관과 같은 화면이다.',
        target: 'admin-orders-tracking',
        keywords: ['배송조회', '송장', '택배', '배송일지', '본발송', '재발송', '배송완료', '배송중'],
      },
      {
        name: '결제확인 취소',
        description:
          '결제확인만 취소하고 주문은 입금 대기로 남긴다. 나중에 다시 확인할 수 있다. 주문을 끝내는 「주문 취소」와 다르다. 발송·클레임이 있으면 되돌릴 수 없다.',
        target: 'admin-orders-unconfirm',
        keywords: ['확인 취소', '입금 대기', '되돌리기', '결제확인 취소'],
      },
      {
        name: '주문 취소',
        description:
          '주문을 끝낸다. 다시 살릴 수 없다. 구매 기관에 취소 메일이 가고, 이미 입금 확인된 주문이면 판매업체에도 메일·문자가 간다. 입금 확인만 잘못 눌렀다면 주문 취소 대신 결제확인 취소를 쓴다.',
        target: 'admin-orders-cancel',
        keywords: ['주문 취소', '환불', '메일'],
      },
      {
        name: '취소·반품 승인',
        description:
          '카드를 펼치면 CANCEL_REQUEST·RETURN_REQUEST 상품 행이 주황 배경으로 강조되고, 그 행의 주 버튼은 「승인」이다. 승인하면 상품이 CANCELED로 전환되고 환불 대기가 자동으로 생성된다. 취소 승인은 재고를 자동 복구하고, 반품 승인은 재고를 복구하지 않는다.',
        target: 'admin-orders-claim-approve',
        keywords: ['취소', '반품', '승인', '클레임', '재고', '환불 대기'],
      },
      {
        name: '취소·반품 반려',
        description:
          '대기 상품 행의 「반려」는 승인 옆 보조 버튼이다. 사유를 입력하고 반려하면 구매 기관에 안내된다. 반려 사유는 필수(255자 이내)이며, 상품은 이전 배송 상태(발송 준비·배송완료 등)로 복귀한다.',
        target: 'admin-orders-claim-reject',
        keywords: ['반려', '사유', '거절', '클레임', '되돌리기'],
      },
      {
        name: '환불 완료 처리',
        description:
          '카드를 펼치면 환불 대기 상품 행의 주 버튼이 「환불 완료 처리」다. 클릭하면 은행·계좌·예금주·메모를 입력하고 송금 결과를 이 자리에서 완료로 표시한다. 별도의 수동 환불 화면으로 이동하지 않는다.',
        target: 'admin-orders-refund-done',
        keywords: ['환불', '송금', '완료', '계좌이체', '수동 환불'],
      },
      {
        name: '주문 조정',
        description:
          '상품 행의 「조정」으로 구매자 요청 없이도 처리한다. 전화 환불은 송금 계좌를 넣고 환불 완료로 표시하거나, 송금 전이면 환불 대기로 둔다. 배송 상태만 신규·발송준비·배송중·배송완료·구매확정으로 바꿀 수도 있다. 사유는 필수이고 내부 메모에 남는다. 이미 환불 완료된 건은 바꿀 수 없다. 송장이 있으면 배송 정보 아래 보조 버튼이고, 클레임·환불 완료 처리가 있는 행에서도 보조로 낮춘다.',
        target: 'admin-orders-adjust',
        keywords: ['조정', '전화 환불', '임의', '상태 변경', 'CS', '환불 완료', '배송 상태'],
      },
      {
        name: '재발송 등록',
        description:
          '오발송·누락으로 택배를 한 번 더 보냈을 때, 상품 행에서 「재발송」으로 다른 송장·수량·사유를 붙인다. 주문번호는 그대로다. 송장이 하나면 송장번호와 배송조회만 한 줄로 보이고, 재발송이 있을 때만 본발송·재발송으로 나뉜다. 같은 송장은 등록되지 않는다. 카톤 단위는 주문 수량일 뿐 배송을 나누지 않는다.',
        target: 'admin-orders-extra-ship',
        keywords: ['추가발송', '재발송', '누락', '오발송', '택배사', '송장', '보충', '카톤'],
      },
    ],
    notes: [
      '상단 필터는 주문 상태(전체·입금 알림·입금 미진행·배송 진행·완료·취소)와 처리할 일(환불 대기·클레임·문의·세금계산서)로 나뉜다. 선택은 한 번에 하나다. 같은 주문은 조건이 겹치면 상태에도, 환불 대기에도, 클레임·문의에도 각각 나타난다. 생명주기 다섯은 주문마다 하나다.',
      '사이드바 「주문 · 입금」 숫자 배지는 입금대기(PENDING)처럼 아직 처리하지 않은 건수다. 정산 배지도 미처리 대기 건수다.',
      '수동 환불·취소·반품 화면은 모두 이 주문 화면으로 통합되었다. 카드에 「취소 대기」/「반품 대기」/「환불 대기 N원」 배지가 붙으면, 카드를 펼쳐 해당 상품 행에서 「승인」/「반려」/「환불 완료 처리」로 마감한다. 기존 /admin/refunds · /admin/claims 는 처리할 일(환불 대기·클레임)으로 이동한다.',
      '취소 요청 승인은 재고를 자동 복구하고, 반품 요청 승인은 재고 복구가 없다. 반품 승인 후 재고는 판매업체가 상품관리 재고 이력에서 직접 입고한다. 승인된 클레임은 상품 상태가 CANCELED로 바뀌고, 그 자리에 「환불 완료 처리」 버튼이 이어서 뜬다.',
      '상품 행 「조정」은 구매자 화면 요청 없이도 된다. 전화 환불은 해당 상품만 취소·환불하고, 배송완료 뒤면 정산에서 반품처럼 차감한다. 발송 전 취소는 재고를 되돌리고 정산 매출에는 안 잡힌다.',
      '카드 요약을 누르면 아래로 상세가 펼쳐진다. 예전 주문 상세 모달은 없다. 세금계산서 모달·조직 상세 팝업은 별도 모달로 뜬다.',
      '입금 미진행 주문이 입금 알림 없이 3일이 지나면 자동 취소된다. 「입금했어요」를 누른 건 확인을 기다린다.',
      '새 주문은 토스페이먼츠로만 결제한다. 계좌이체 주문은 더 만들지 않고, 이미 접수된 계좌이체 판매기록은 결제수단 그대로 둔다.',
      '주문 다음날 오전 9시 이후, 아직 결제하지 않은 대기 주문에는 구매자 연락처로 안내 문자가 한 번 간다. 이전 계좌이체 주문은 「입금했어요」를 누르기 전 건에 미입금 안내가 간다.',
      '구매 기관이 「입금했어요」를 누르면 운영 휴대폰(010-4385-1031)으로 입금 알림 문자가 간다.',
      '구매 기관은 배송 시작 전(입금확인·출고준비)까지 환불신청할 수 있다. 신청하면 주문이 바로 취소되어 「취소」에 보이고, 송금이 남으면 「처리할 일 · 환불 대기」에서도 모은다. 발송준비 중 환불신청 시 판매업체·운영 휴대폰에 문자가 간다. 배송중에는 환불신청 불가이고 배송완료 후 반품만 가능하다.',
      '입금 전 주문은 「주문 취소」로 끝낸다. 입금 확인 뒤에는 「환불신청」이다.',
      '결제확인 메일·문자는 판매업체가 「신규 주문」 알림을 켜 두고 이메일·휴대폰을 등록한 경우에만 간다. 구매 기관에는 입금 확인·발송·주문 취소 메일이 간다.',
      '결제확인 취소는 주문을 입금 대기로 남긴다. 주문 취소는 주문을 끝낸다. 결제확인 취소는 신규 주문(미발송)일 때만 된다.',
      '주문을 취소하면 세금계산서 발행 요청도 함께 취소된다.',
      '이 화면의 처리현황은 배송이 끝났는지만 본다. 구매확정·정산은 여기서 다루지 않는다.',
    ],
  },
  'admin-sellers': {
    title: '판매업체',
    summary: '관리자가 아이디·임시 비밀번호만으로 계정을 만들고, 온보딩·승인·이용 상태를 관리한다.',
    audience: 'admin',
    purpose: '판매 채널에 참여하는 업체를 통제한다.',
    keywords: ['입점', '온보딩', '판매자 등록', '임시비밀번호'],
    features: [
      {
        name: '상태 요약',
        description: '승인 대기·이용중·정지·반려 등 상태로 바로 필터한다.',
        target: 'admin-sellers-kpis',
      },
      {
        name: '검색·등록',
        description: '업체명·사업자번호·아이디로 찾고, 판매업체 계정을 새로 등록한다.',
        target: 'admin-sellers-toolbar',
        keywords: ['등록', '계정생성'],
      },
      {
        name: '판매업체 등록',
        description:
          '아이디·임시 비밀번호만으로 PENDING 계정을 만든다. 사업자·정산 정보는 업체가 온보딩에서 입력한 뒤 승인한다.',
        target: 'admin-sellers-create',
      },
      {
        name: '업체 목록',
        description:
          '온보딩 여부·서명을 확인하고 승인·반려·정지를 처리한다. 상태가 바뀌면 담당자 이메일로 결과를 보낸다. 온보딩 전 업체명은 아이디로 표시되며, 온보딩 미완료면 승인할 수 없다.',
        target: 'admin-sellers-list',
        keywords: ['서명', '온보딩', '승인메일'],
      },
      {
        name: '계약서',
        description:
          '공급계약서를 새 창에서 확인하고, 「PDF로 저장」 시 갑·을·서명 조판 계약서 PDF를 연다. 서명 전이면 본문만, 온보딩 완료면 서명까지 본다.',
        target: 'admin-sellers-contract',
        keywords: ['계약서', '서명', '약관', '공급계약', 'PDF'],
      },
    ],
    notes: [
      '공개 자가가입은 없다. 관리자는 계정만 만들고, 업체가 로그인 후 온보딩한다.',
      '온보딩 완료 후에도 PENDING이며 관리자 승인 후 판매 가능하다. 승인·반려·정지 결과는 담당자 이메일로 간다.',
    ],
  },
  'admin-legal-docs': {
    title: '약관 CMS',
    summary: '판매·기관 이용약관과 공급계약서 본문을 버전 단위로 등록하고 활성 버전을 지정한다.',
    audience: 'admin',
    purpose: '온보딩·공개 약관·계약 화면이 최신 정책을 쓰도록 한다.',
    keywords: ['이용약관', '개인정보', '정산규정', '계약서', '공급계약'],
    features: [
      {
        name: '목록·필터',
        description: '대상(판매/기관)별로 약관·계약서 버전을 본다.',
        target: 'admin-legal-docs-toolbar',
      },
      {
        name: '새 버전',
        description: '코드·버전·구분(이용약관/계약서)·본문을 추가하고 활성화한다.',
        target: 'admin-legal-docs-create',
        keywords: ['계약서', '구분'],
      },
      {
        name: '약관 목록',
        description:
          '기존 버전을 열어 본문·구분·활성 여부를 수정한다. 편집 모달에서 제목·본문·시행일·활성 상태를 저장한다.',
        target: 'admin-legal-docs-list',
        keywords: ['편집', '버전', '활성', '계약서'],
      },
    ],
    notes: [
      '약관 편집 폼은 「새 버전」또는 목록의 편집을 눌렀을 때만 열린다.',
      '판매업체 온보딩은 이용약관 동의 후 계약서(구분: 계약서)를 읽고 서명한다.',
      '이미 동의한 버전의 본문은 수정할 수 없다. 내용 변경 시 새 버전을 등록·활성화한다.',
    ],
  },
  'admin-content-snippets': {
    title: '화면 멘트',
    summary: '몰·포털·판매 화면의 고정 안내 문구를 키 단위로 수정한다.',
    audience: 'admin',
    purpose: '배송·교환·결제·서비스 안내를 배포 없이 갱신한다.',
    keywords: ['멘트', '스니펫', '배송안내', '교환반품', '이벤트배너', '홈배너', '당일출고', '출고마감'],
    features: [
      {
        name: '그룹 필터',
        description: 'mall/portal/seller 그룹으로 목록을 좁힌다.',
        target: 'admin-content-snippets-toolbar',
      },
      {
        name: '멘트 목록',
        description:
          '키·제목·미리보기를 보고 편집을 연다. 편집 모달에서 제목·형식·본문을 저장하고 미리보기를 확인한다.',
        target: 'admin-content-snippets-list',
        keywords: ['편집', 'HTML', '미리보기'],
      },
    ],
    notes: [
      'mall.home.promo_html 키에 HTML을 넣으면 몰 홈 히어로 아래 이벤트 안내가 노출된다. 비우면 숨긴다.',
      'mall.product.same_day_cutoff 는 상품상세·스토어의 당일 출고 한 줄이다. {cutoff} 자리에 판매업체 마감 시각이 들어간다. 비우면 숨긴다.',
      'mall.product.return_policy 는 판매업체가 교환·반품 안내를 비워 둔 상품상세의 보조 멘트다. 반품비는 판매업체 설정을 우선한다.',
      'mall.cart.shipping_note 는 상품 배송비(여러 상품이면 최댓값) 안내와 맞춰 둔다.',
      '멘트 편집·미리보기는 목록에서 편집을 눌렀을 때만 열린다.',
    ],
  },
  'admin-seller-view': {
    title: '판매업체 화면 보기',
    summary: '최고관리자가 판매업체 아이디로 판매관리센터를 연다.',
    audience: 'admin',
    purpose: '비밀번호 없이 해당 업체가 보는 화면을 그대로 확인한다.',
    keywords: ['최고관리자', '판매업체', '화면보기', '아이디', '가장', '대신보기'],
    features: [
      {
        name: '업체 검색',
        description: '아이디·상호·업체코드로 목록을 좁힌다.',
        target: 'admin-seller-view-toolbar',
      },
      {
        name: '업체 목록',
        description: '상호·아이디·상태를 보고 열 업체를 고른다.',
        target: 'admin-seller-view-list',
      },
      {
        name: '이 업체로 보기',
        description:
          '선택한 판매업체 권한으로 판매관리센터를 새 탭에서 연다. 저장·처리도 그 업체로 반영된다.',
        target: 'admin-seller-view-open',
      },
    ],
    notes: [
      '권한 100(최고관리자)만 메뉴가 보인다.',
      '관리자 로그인은 유지되고, 판매업체 세션만 바뀐다.',
      '승인·심사 중인 업체만 열 수 있다. 정지·반려·탈퇴는 열리지 않는다.',
      '판매관리센터 상단에서 「관리자로 돌아가기」를 누르면 판매업체 보기만 끝난다.',
    ],
  },
  'seller-onboarding': {
    title: '입점 온보딩',
    summary:
      '관리자가 만든 계정으로 첫 로그인하면 사업자·정산·약관·계약서·서명을 모두 입력·완료한다.',
    audience: 'seller',
    purpose: '검수에 필요한 사업자·정산 정보와 약관·공급계약 수락을 받는다.',
    keywords: ['서명', '약관', '계약서', '임시비밀번호', '입점', '공급계약'],
    features: [
      {
        name: '안내',
        description: '온보딩 목적과 승인 대기 절차를 안내한다.',
        target: 'seller-onboarding-intro',
      },
      {
        name: '사업자 정보',
        description:
          '업체명·상호·사업자번호·대표자·담당자·출고지를 입력하고, 이메일·휴대폰 인증을 모두 완료한다.',
        target: 'seller-onboarding-profile',
        keywords: ['이메일인증', '휴대폰인증', '본인인증'],
      },
      {
        name: '정산 계좌',
        description: '정산 은행·계좌·예금주를 입력한다.',
        target: 'seller-onboarding-bank',
      },
      {
        name: '약관 동의',
        description: '필수 이용약관 전문을 확인하고 각각 동의한다. 체크는 미리 선택되지 않는다.',
        target: 'seller-onboarding-terms',
      },
      {
        name: '계약서 확인',
        description:
          '기본계약(제1~16조)·계약당사자·별첨을 섹션별로 확인하고 각 체크·전자계약·최종 동의 후, 갑·을·서명 조판 PDF 파일을 바로 내려받는다.',
        target: 'seller-onboarding-contract',
        keywords: ['계약서', '기본계약', '별첨', '체크', 'PDF'],
      },
      {
        name: '서명',
        description: '약관·계약 동의 의사로 대표자 또는 계약권한자가 캔버스에 서명한다.',
        target: 'seller-onboarding-signature',
      },
      {
        name: '검수 요청',
        description:
          '정보·약관·계약·서명을 제출하면 PENDING 검수 대기가 된다. 승인 전에는 판매할 수 없다.',
        target: 'seller-onboarding-submit',
        keywords: ['제출', '검수'],
      },
      {
        name: '입력 확인',
        description:
          '스크롤해도 따라오는 체크리스트로 미입력·미인증·미동의 항목을 확인한다. 계약서는 기본계약·계약당사자·별첨·전자계약·최종 동의를 각각 표시하며, 누르면 해당 칸으로 이동한다.',
        target: 'seller-onboarding-tracker',
        keywords: ['체크리스트', '진행', '스크롤', '입력확인', '계약서'],
      },
    ],
    notes: [
      '온보딩 완료만으로는 판매할 수 없다. 관리자 승인이 필요하며, 승인·반려·정지는 담당자 이메일로 안내한다.',
      '임시 비밀번호로 들어온 경우에만 비밀번호 변경 칸이 보인다.',
      '계약서 본문은 약관 CMS(구분: 계약서)에서 관리하며, 활성 버전이 온보딩에 노출된다.',
      '온보딩 완료 시 약관·계약 본문과 서명, 계약서 PDF가 체결 스냅샷으로 저장된다. 이후 CMS에서 계약 내용을 바꿔도 이미 체결한 업체의 기록은 변하지 않는다.',
      '동의가 있는 CMS 버전은 본문 수정을 막고 새 버전 등록을 안내한다.',
      '넓은 화면에서는 오른쪽 입력 확인 패널이 sticky로 따라다닌다.',
    ],
  },
  'admin-members': {
    title: '고객관리',
    summary: '가입 기관을 고객으로 보고, 주문 이력을 확인한다.',
    audience: 'admin',
    purpose: '함우리몰 고객을 한곳에서 확인한다.',
    keywords: ['고객관리', '회원', '구매기관'],
    features: [
      {
        name: '고객 요약',
        description: '고객 수와 주문한 기관 수를 본다.',
        target: 'admin-members-kpis',
      },
      {
        name: '검색',
        description: '시설명·아이디·대표자·기관기호로 검색한다.',
        target: 'admin-members-toolbar',
      },
      {
        name: '고객 목록',
        description: '가입일·주문 건수·최근 주문일을 본다.',
        target: 'admin-members-list',
      },
    ],
    notes: [
      '일반회원 가입은 아이디·비밀번호(또는 카카오)만 받는다. 배송지·연락처는 주문 때, 환불계좌는 취소·반품 때 받는다.',
      '탈퇴한 일반회원은 별도 보관 테이블에 남고, 이 목록에는 나오지 않는다. 카카오 연결은 끊어 다시 가입할 수 있다.',
    ],
  },
  'admin-cs': {
    title: '문의 · 후기',
    summary: '1:1 상담과 상품 문의·구매후기를 관리한다.',
    audience: 'admin',
    purpose: '몰·포털 공용 상담 답변과 상품 CS 중재를 한곳에서 처리한다.',
    keywords: ['1:1상담', '상담게시판', '상품문의', '구매후기', 'CS', '숨김', '답변', '관련주문', '주문번호', '답변 위임', '판매자 위임', '권한 위임'],
    features: [
      {
        name: '현황 요약',
        description: '미답변 상품문의·후기·1:1 상담 건수를 본다.',
        target: 'admin-cs-kpis',
      },
      {
        name: '상담·문의 탭',
        description: '1:1 상담, 상품 문의, 구매후기를 나눈다.',
        target: 'admin-cs-tabs',
      },
      {
        name: '상담 채널 탭',
        description: '1:1 상담을 「전체 · 포털 상담 · 몰 문의」로 나눠 본다. 포털 상담은 임금·회계 등 사업 상담이고, 몰 문의는 함우리몰에서 주문번호를 첨부해 접수된 상담이다. 카이온 홍보메일·설명회 신청은 이 목록에 나오지 않고 카이온 상담 화면에서만 본다. 탭마다 답변대기 건수를 함께 보여 준다.',
        target: 'admin-cs-channel',
        keywords: ['포털 상담', '몰 문의', '채널'],
      },
      {
        name: '검색·필터',
        description: '상담은 답변대기·답변완료·판매자 위임으로 좁히고, 제목·ID·기관·주문번호로 찾는다. 상품 CS는 미답변·키워드로 좁힌다.',
        target: 'admin-cs-toolbar',
        keywords: ['판매자 위임', '답변대기', '답변완료', '상태'],
      },
      {
        name: '목록',
        description: '상담은 상세·답변, 상품 문의는 답변·상품 열기·숨김, 후기는 상품 열기·숨김을 한다. 1:1 상담 상태 열에는 답변대기·답변완료와 함께, 판매업체에 답변을 맡긴 건은 「답변대기 · 위임」「답변완료 · 위임」으로 바로 보인다. 1:1 상담 제목 옆에는 「포털」「몰」 채널 배지가 붙고, 테스트 고객 글에는 「테스트」 배지가 붙는다. 1:1 상담·상품 문의·후기에는 「관련 주문」이 붙어, 주문 건이면 관리자 주문 페이지로 이동할 수 있다. 주문 화면 「클레임 · 문의」에서 넘어오면 해당 주문번호로 상품 문의가 검색된다. 후기는 작성 시 주문을 저장하고, 기존 건은 같은 회원·상품의 구매 주문으로 채운다.',
        target: 'admin-cs-list',
      },
      {
        name: '1:1 상담 상세·답변',
        description: '상담 내용 확인 후 답변을 등록하고 Excel을 첨부할 수 있다. 주문 건이면 관련 주문을 바로 연다. 상세를 열면 AI가 문의에 맞는 답변 초안을 실시간으로 만든다. 답변 목록에는 관리자·판매자 역할 배지가 붙어 누가 응답했는지 구분된다.',
        target: 'admin-cs-consult-detail',
      },
      {
        name: '판매업체 답변 위임',
        description: '주문번호가 붙은 1:1 상담에 한해, 상세 하단의 「판매업체에게 답변 위임」 스위치로 그 주문의 판매업체가 판매관리센터에서 직접 답변할 수 있게 위임/회수한다. 위임된 상담은 목록 상태값에 「답변대기 · 위임」「답변완료 · 위임」으로 표시되고, 상태 필터에서 「판매자 위임」만 모아 볼 수 있다. 이미 답변완료인 상담도 위임하면 판매업체가 추가 답변을 남길 수 있다. 언제든 회수할 수 있고, 위임 이후에도 관리자 답변은 그대로 등록할 수 있다.',
        target: 'admin-cs-consult-delegate',
        keywords: ['답변 위임', '판매자 답변', '권한 위임', '위임 회수'],
      },
      {
        name: '상품문의 답변',
        description: '미답변 상품문의를 열고 판매업체를 대신해 답변을 등록한다. 이미 답변이 있으면 다시 등록하지 않는다.',
        target: 'admin-cs-qna-reply',
      },
    ],
    notes: [
      '상담은 채널이 나뉜다. 장기요양지원센터 「상담 신청」은 PORTAL, 함우리몰 「상담 문의」는 주문번호를 첨부한 MALL 상담으로 저장된다. 주문번호가 없는 몰 상담은 받지 않는다. 같은 테이블을 사용하지만 채널로 구분하며, 판매업체에는 MALL 채널 중 자기 주문 건만 보인다.',
      '포털 1:1 상담은 비회원도 작성할 수 있다. 비회원은 신청만 하고 내역은 보지 못한다. 로그인 회원이 쓰면 작성자 ID는 회원 아이디가 들어가고, 「상담 신청 내역」에서 본인 글만 열람·삭제한다. 함우리몰 문의는 로그인 회원만 주문번호를 첨부해 작성한다.',
      '테스트 고객(testemp)이 쓴 1:1 상담은 최고관리자(role_level 100)에게만 보인다. 일반 관리자 목록·건수·상세에서 빠지고, 판매업체에게도 보이지 않는다. 최고관리자 화면에서는 「테스트」 배지로 구분된다.',
      '구매자 마이페이지는 「문의 내역」 하나다. 상품에서 남긴 문의와 주문번호를 첨부한 문의를 한 목록에서 보고, 새 문의도 그 화면에서 작성한다. 판매·관리 화면은 답변 주체가 달라 상품문의와 몰 상담을 구분해 처리한다. 상품문의 답변은 판매업체와 관리자 모두 등록할 수 있고, 후기 답변은 판매관리센터에서 하며 관리자는 후기 숨김만 한다.',
      '함우리몰 상담 문의는 주문번호가 있을 때만 받는다. 그 상담은 상세에서 「판매업체 답변 위임」을 켜서 해당 주문의 판매업체에게 답변 권한을 넘길 수 있다. 이미 답변완료인 상담도 위임하면 판매업체가 추가 답변을 남길 수 있다. 위임 없이 판매업체가 답변을 시도하면 서버가 403 으로 거절한다.',
      'PG·쿠폰·포인트·접속통계 등은 운영하지 않는다.',
    ],
  },
  'admin-settings': {
    title: '입금 계좌 · 운영 공지',
    summary: '이전 계좌이체 주문에 안내하던 입금 계좌와 몰 홈 운영 공지를 관리한다. 새 주문은 토스페이먼츠로만 결제한다.',
    audience: 'admin',
    purpose: '남은 계좌이체 주문 안내와 운영 공지 문구를 최신으로 유지한다.',
    features: [
      {
        name: '입금 계좌 · 공지',
        description: '은행·계좌·안내 문구·운영 공지를 저장한다.',
        target: 'admin-settings-form',
      },
      {
        name: '미리보기',
        description: '구매 기관에게 보이는 형태를 확인한다.',
        target: 'admin-settings-preview',
      },
    ],
  },
  'admin-settlements': {
    title: '판매업체 정산',
    summary: '판매업체가 묶어 요청한 정산을 확인하고, 송금 후 입금 완료로 표시한다.',
    audience: 'admin',
    purpose: '판매업체가 고른 정산 요청 묶음을 입금하고 엑셀로 대조한다.',
    keywords: [
      '지급일',
      '송금',
      '정산금',
      '주문정산',
      '정산처리일자',
      '미정산',
      '정산해제',
      '일괄정산',
      '처리이력',
      '함우리마진',
      '판매업체마진',
      '배송비',
    ],
    features: [
      {
        name: '현황 요약',
        description: '전체·정산 완료·미정산 탭으로 목록을 나누고, 미정산 지급액 합계를 본다. 기본은 전체.',
        target: 'admin-settle-kpis',
      },
      {
        name: '연월·상태·검색',
        description: '구매확정 연월·미정산/완료·판매업체·주문번호로 대상을 좁힌다.',
        target: 'admin-settle-toolbar',
      },
      {
        name: '주문건별 정산 목록',
        description: '판매업체가 요청한 묶음의 이름·건수·지급액·계좌를 보고 엑셀을 받는다. 송금 후 입금 완료로 표시한다.',
        target: 'admin-settle-list',
      },
      {
        name: '일괄 처리',
        description: '요청 상태인 묶음을 입금 완료로 표시하거나, 잘못 처리한 입금을 사유와 함께 되돌린다.',
        target: 'admin-settle-bulk',
      },
      {
        name: '주문 상세',
        description: '주문번호를 누르면 주문·입금 화면과 같은 주문 상세를 연다.',
        target: 'admin-settle-order',
      },
      {
        name: '정산 처리',
        description: '송금 후 선택한 주문을 정산 완료로 표시한다. 처리자와 시각이 이력에 남는다.',
        target: 'admin-settle-pay',
      },
      {
        name: '정산 해제',
        description: '잘못 처리한 정산을 사유와 함께 미정산으로 되돌린다. 해제자도 이력에 남는다.',
        target: 'admin-settle-reopen',
      },
      {
        name: '처리 이력',
        description: '정산·해제 일시·처리자·사유를 주문별 또는 전체로 조회한다.',
        target: 'admin-settle-logs',
      },
    ],
    notes: [
      '정산 요청은 판매업체가 구매확정 건과 반품 정산(변심 반품비, 남은 출고 배송비)을 골라 만듭니다. 관리자가 건을 골라 정산하지 않습니다.',
      '입금 완료는 요청 당시 금액이 그대로일 때만 됩니다. 그 사이 금액이 바뀌면 요청을 다시 받습니다.',
      '지급액 = 판매대금 − 함우리 마진 + 고객이 낸 배송비. 배송비는 주문당 해당 업체에 한 번만 붙는다.',
      '전량 취소로 고객에게 배송비를 돌려준 주문은 배송비를 넣지 않는다.',
      '정산 계좌가 없는 업체 주문은 정산 처리할 수 없다.',
      '정산·해제는 모두 감사 이력에 남으며 「처리 이력」에서 확인한다.',
      '우측 상단 「도움말」에서 Ctrl+K로 기능을 검색할 수 있다.',
    ],
  },
}

const ADMIN_VISIT_STATS_FEATURE: FeatureSpecItem = {
  name: '방문자 통계',
  description:
    '상단 오른쪽 「방문자 통계」를 누르면 오늘·어제·누적 방문자와 최근 7일 추이를 모달로 본다.',
  target: 'admin-visit-stats',
  keywords: ['방문', '방문자', '페이지뷰', '통계'],
}

for (const spec of Object.values(MANAGE_FEATURE_SPECS)) {
  if (spec.audience !== 'admin') continue
  if (spec.features.some((feature) => feature.target === 'admin-visit-stats')) continue
  spec.features.unshift(ADMIN_VISIT_STATS_FEATURE)
}

export function featureSpecOf(routeName: string | symbol | null | undefined): FeatureSpec | null {
  if (!routeName || typeof routeName !== 'string') return null
  return MANAGE_FEATURE_SPECS[routeName] || null
}

/**
 * 검색 결과 클릭 시 실제로 이동할 라우트.
 * 상품 편집처럼 필수 파라미터가 있으면 목록(또는 등록 폼)으로 보낸다.
 */
export function resolveFeatureSpecNavigation(routeName: string): {
  name: string
  query?: Record<string, string>
} {
  if (routeName === 'admin-product-edit') return { name: 'admin-products' }
  if (routeName === 'seller-product-edit') return { name: 'seller-products' }
  if (routeName === 'seller-product-new') {
    return { name: 'seller-products', query: { form: 'new' } }
  }
  if (routeName === 'admin-info') return { name: 'info-plaza' }
  return { name: routeName }
}

function normalizeSearchText(value: string) {
  return value
    .toLowerCase()
    .replace(/[·・]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function tokenizeQuery(query: string) {
  return normalizeSearchText(query)
    .split(/[\s,/|]+/)
    .map((token) => token.trim())
    .filter((token) => token.length >= 1)
}

function fieldMatchScore(haystack: string, tokens: string[], weight: number) {
  const text = normalizeSearchText(haystack)
  if (!text || !tokens.length) return 0
  let hit = 0
  for (const token of tokens) {
    if (text.includes(token)) hit += 1
  }
  if (!hit) return 0
  const coverage = hit / tokens.length
  const bonus = tokens.some((token) => text.startsWith(token) || text === token) ? 0.15 : 0
  return weight * (coverage + bonus)
}

function blobHasAllTokens(blob: string, tokens: string[]) {
  const text = normalizeSearchText(blob)
  return tokens.every((token) => text.includes(token))
}

/**
 * 기능정의서를 키워드로 검색한다.
 * audience를 주면 해당 포털(판매/관리자) 화면만 본다.
 * 두 단어 이상이면 해당 항목 텍스트에 모든 토큰이 있어야 한다.
 */
export function searchFeatureSpecs(
  query: string,
  options: { audience?: 'seller' | 'admin'; limit?: number } = {},
): FeatureSearchHit[] {
  const tokens = tokenizeQuery(query)
  if (!tokens.length) return []

  const limit = options.limit ?? 24
  const hits: FeatureSearchHit[] = []
  const requireAll = tokens.length >= 2

  for (const [routeName, spec] of Object.entries(MANAGE_FEATURE_SPECS)) {
    if (options.audience && spec.audience !== options.audience) continue

    const pageKeywordText = (spec.keywords || []).join(' ')
    const pageBlob = [spec.title, spec.summary, spec.purpose, pageKeywordText, ...(spec.notes || [])].join(
      ' ',
    )
    const pageScore =
      fieldMatchScore(spec.title, tokens, 100) +
      fieldMatchScore(spec.summary, tokens, 48) +
      fieldMatchScore(spec.purpose, tokens, 28) +
      fieldMatchScore(pageKeywordText, tokens, 70)

    if (pageScore > 0 && (!requireAll || blobHasAllTokens(pageBlob, tokens))) {
      hits.push({
        routeName,
        audience: spec.audience,
        pageTitle: spec.title,
        kind: 'page',
        score: pageScore,
        title: spec.title,
        snippet: spec.summary,
        matchedIn: '화면',
      })
    }

    for (const feature of spec.features) {
      const featureBlob = [
        spec.title,
        feature.name,
        feature.description,
        ...(feature.keywords || []),
        feature.target || '',
        pageKeywordText,
      ].join(' ')
      const featureScore =
        fieldMatchScore(feature.name, tokens, 90) +
        fieldMatchScore(feature.description, tokens, 42) +
        fieldMatchScore((feature.keywords || []).join(' '), tokens, 65) +
        fieldMatchScore(feature.target || '', tokens, 20)

      if (featureScore > 0 && (!requireAll || blobHasAllTokens(featureBlob, tokens))) {
        hits.push({
          routeName,
          audience: spec.audience,
          pageTitle: spec.title,
          kind: 'feature',
          score: featureScore + pageScore * 0.12,
          title: feature.name,
          snippet: feature.description,
          matchedIn: '기능',
          featureName: feature.name,
          target: feature.target,
        })
      }
    }

    for (const note of spec.notes || []) {
      const noteScore = fieldMatchScore(note, tokens, 22)
      if (noteScore > 0 && (!requireAll || blobHasAllTokens(`${spec.title} ${note}`, tokens))) {
        hits.push({
          routeName,
          audience: spec.audience,
          pageTitle: spec.title,
          kind: 'note',
          score: noteScore + pageScore * 0.08,
          title: `${spec.title} · 참고`,
          snippet: note,
          matchedIn: '참고',
        })
      }
    }
  }

  return hits
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score
      if (a.kind !== b.kind) {
        const order = { feature: 0, page: 1, note: 2 }
        return order[a.kind] - order[b.kind]
      }
      return a.title.localeCompare(b.title, 'ko')
    })
    .slice(0, limit)
}
